import { FunctionComponent } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import cn from 'classnames';

import { CastButton } from '../../components/buttons/CastButton';
import { AirPlayButton } from '../../components/buttons/AirPlayButton';
import { PipButton } from '../../components/buttons/PipButton';
import { FullscreenButton } from '../../components/buttons/FullscreenButton';
import { TogglePlayButton } from '../../components/buttons/TogglePlayButton';
import { usePlayerInteraction } from '../../hooks/usePlayerInteraction';
import { usePlayerState } from '../../hooks/usePlayerState';
import { usePreviewElement } from '../../hooks/usePreviewElement';
import { useCast } from '../../hooks/CastProvider';
import { VideoAdLink } from '../../components/VideoAdLink/VideoAdLink';
import { useViewportType } from '../../hooks/useViewportType';
import { AdData } from '../../hooks/useAdData';
import { IconButton } from '../../components/IconButton/IconButton';
import { VolumeButton } from '../../components/VolumeButton/VolumeButton';

export interface AdLayoutProps {
  adData?: AdData;
}

export const AdLayout: FunctionComponent<AdLayoutProps> = ({ adData }) => {
  const layoutRef = useRef<HTMLDivElement>(null!);
  const state = usePlayerState();
  const { isActive: isCastActive, castingType } = useCast();
  const previewElement = usePreviewElement();
  const { size } = useViewportType();

  const isChromecastActive = isCastActive && castingType === 'chromecast';

  useEffect(() => {
    if (!isChromecastActive || !previewElement) {
      return;
    }

    previewElement.classList.add('vgp-preview-cast');

    return () => {
      previewElement.classList.remove('vgp-preview-cast');
    };
  }, [isChromecastActive, previewElement]);

  const { isActive } = usePlayerInteraction({
    layoutRef,
  });

  const isVisible = isChromecastActive || isActive || state === 'paused';
  return (
    <div
      className={cn('vgp-ad-layout', { 'vgp-ad-layout-hidden': !isVisible })}
      ref={layoutRef}
    >
      <div className="vgp-buttons vgp-ad-button-group">
        <CastButton variant="square" filled color="video-ui" />
        <AirPlayButton variant="square" filled color="video-ui" />
        <PipButton variant="square" filled color="video-ui" />
        <FullscreenButton
          color="video-ui"
          filled
          className="vgp-ad-fullscreen-button"
        />
      </div>
      <div className="vgp-ad-layout-row">
        <div
          className={
            size === 'xs'
              ? 'vgp-ad-playback-group-small'
              : 'vgp-ad-playback-group'
          }
        >
          <TogglePlayButton color="accent" />
          {adData && <VideoAdLink adData={adData} />}
        </div>
        <div className="vgp-ad-buttons-column">
          <IconButton color="video-ui">
            <VolumeButton className="vgp-volume-button" />
          </IconButton>
        </div>
      </div>
    </div>
  );
};
